import { Icon } from "@material-ui/core"
import _ from "lodash"
import React, { Fragment, useMemo, useEffect, useState } from "react"

import { AirportSeasonConfigurations } from "../../api/AirportSeasonConfiguration"
import { Flight } from "../../api/Flight"

import { ValueLabel } from "../generic/fields"
import SeasonDateRange from "../SeasonDateRange"

import {
  AircraftRegistrationField,
  AircraftTypeField,
  DOOPField,
  DropdownBooleanField,
  HandlerField,
  MinGroundTimeFieldN,
  MtowField,
  NoiseCertificateField,
  OperatorDropdownField,
  OperatorField,
  OpTextField,
  OvernightIndicatorFieldN,
  ParkingLocationField,
  RequestCodeField,
  SeatsField,
  ServiceNumberField,
  ServiceTypeDropdownField,
  StationField,
  TerminalDropdownField,
  TimeField,
  WOOPFieldN
} from "../selectorFields"

import { getFlightModeText, getFlightSelectorConfiguration } from "../Utils"
import ReplyCodeField from "../ReplyCodeField"
import {
  FlightMode,
  EditMode,
  ActionType,
  PageGroup,
  GCRMode,
  fieldOrder
} from "../../common/transaction"
import DropdownField from "../generic/form/DropdownField"
import { FieldName, FlightText } from "../../common/text"
import TransactionFlightHeaders from "./TransactionFlightHeaders"
import TimeRangeField from "../generic/form/TimeRangeField"
import TextField from "../generic/form/TextField"
import {
  isFieldEditable,
  showField,
  showFieldSection,
  showOperatorField
} from "./Config"
import { getTimeAndCodeFilteredAirportSeasons } from "../../components/Utils"
import SeasonDatePicker from "../SeasonDatePicker"
import { CodeType } from "../AirportSelector"
import { serviceNoRegex } from "../../common/regex"
import { ErrorText } from "../../common/errorText"
import { ImainTransactions } from "./TransactionItem"
import { dateRangeIsValid, lockedOrDisabled } from "../PageConfigUtils"
import Button from "../generic/buttons/Button"
import { useAppSelector } from "../../app/hooks"
import { RadioBooleanField } from "../generic/form/RadioBooleanField"
import SeasonDepartureDatePicker from "../SeasonDepartureDatePicker"

interface Props extends ImainTransactions {
  idx: number
  flight: Flight
  transId: number
  grcMode: string
  editMode: EditMode
  pageGroup: PageGroup
  airportSeasons: AirportSeasonConfigurations
  codeType: CodeType
  isLastFlight: boolean
  actionType?: ActionType
  inFocus?: boolean
  isInitResponse?: boolean
  fieldsWithChanges?: string[]
  hasMultiFlightModes?: boolean
  showTimeRangeError?: boolean
  onFillDown?: (field: FieldName) => void
  isEditSeries?: boolean
  onChange?: (
    flight: Flight,
    error?: boolean,
    field?: string | string[],
    val?: any
  ) => void
  placement?: string
}

const TransactionFlight: React.FunctionComponent<Props> = ({
  idx,
  flight,
  transId,
  airportSeasons,
  inFocus,
  editMode,
  grcMode,
  codeType,
  pageGroup,
  actionType,
  isInitResponse,
  fieldsWithChanges,
  hasMultiFlightModes,
  isLastFlight,
  onChange,
  placement,
  touched,
  showTimeRangeError,
  onFillDown,
  dateHasError,
  dateHasErrText,
  customShowLabelConfig,
  isEditSeries
}) => {
  const { season, useLocalTime } =
    useAppSelector(
      (state) => state["selectionReducer"].airportSeasonsSelector
    ) || {}
  const { versionInfo, serviceTypes } = useAppSelector(
    (state) => state["appReducer"]
  )

  const { showOptionalFields, useSSIMView } = useAppSelector(
    (state) => state["selectionReducer"].userSettings
  )

  const [hasSelectedStartDate, setHasSelectedStartDate] = useState(false)

  const isSSIMVIew = pageGroup === PageGroup.HANDLING ? false : useSSIMView

  // Label Text
  const {
    operator,
    serviceNo,
    seats,
    clearTime,
    reqTime,
    st,
    servType,
    f,
    mgt,
    dest,
    acReg,
    reason,
    origin,
    last,
    next,
    o,
    acType,
    searchTimeRange
  } = FlightText

  //Field names
  const {
    airport,
    operatorArr,
    operatorDep,
    serviceNoArr,
    serviceNoDep,
    serviceTypeArr,
    serviceTypeDep,
    startDate,
    endDate,
    reqDate,
    doop,
    woop,
    clearedTimeArr,
    clearedTimeDep,
    requiredTimeArr,
    requiredTimeDep,
    requiredDateDep,
    requiredDateArr,
    reasonCodeArr,
    reasonCodeDep,
    turnaroundDays,
    minGroundTime,
    aircraftType,
    numSeats,
    aircraftRegistration,
    originStation,
    destinationStation,
    lastStation,
    nextStation,
    offerTimeArr,
    offerTimeArrTo,
    offerTimeArrFrom,
    offerTimeDep,
    offerTimeDepTo,
    offerTimeDepFrom,
    requestCode,
    origDest,
    lastNext,
    handler,
    vfr,
    towbar,
    gabaLounge,
    operatorText,
    mtow,
    refuelling,
    parkingLocation,
    noiseCertificate,
    departureDate,
    terminalArr,
    terminalDep
  } = FieldName

  const showSection = (fieldName: string) => {
    return showFieldSection(
      fieldName,
      editMode,
      pageGroup,
      versionInfo.handlingApplication !== ""
        ? versionInfo.handlingApplication
        : undefined
    )
  }

  const isDateRange = showSection(startDate) && showSection(endDate)
  const lockedOrDisabledArr = lockedOrDisabled(
    airportSeasons,
    flight.airport,
    season
  )
  const dateRangeError = dateRangeIsValid(flight.startDate, flight.endDate)

  const airportError = lockedOrDisabledArr.lockedOrDisabledArr.includes(
    flight.airport
  )
  const handleFieldChange = (name: string, value: any, error: boolean) => {
    const selectedAirportValue = name === "airport" ? value : ""
    const selectedAirportError =
      lockedOrDisabledArr.lockedOrDisabledArr.includes(selectedAirportValue)

    const updFlight = _.cloneDeep(flight)
    updFlight[name] = value

    if (name === "startDate" && !isDateRange) {
      updFlight.endDate = value
      updFlight.doop = ""
    }
    if (name === "turnaroundDays" && showSection(departureDate)) {
      setHasSelectedStartDate(false)
    }

    onChange && onChange(updFlight, selectedAirportError || error, name, value)
  }

  const fieldHasChanges = (fieldName: string): boolean => {
    return fieldsWithChanges?.includes(fieldName)
  }

  const config = getFlightSelectorConfiguration(
    airportSeasons,
    season,
    [flight.airport],
    codeType
  )
  const isTurnaround = flight.flightMode === FlightMode.Turnaround
  const isArrival = flight.flightMode === FlightMode.Arrival
  const isResponse = editMode === EditMode.RESPONSE
  const isAdd = editMode === EditMode.ADD
  const isEdit = editMode === EditMode.EDIT
  const isRegMode = grcMode === GCRMode.REG
  const isFirstRow = idx === 0
  const isGaba = pageGroup === PageGroup.GABA
  const isHandling = pageGroup === PageGroup.HANDLING
  const isShl = pageGroup === PageGroup.SHL
  const useICAOCodeType = codeType === CodeType.ICAO
  const isOffer = flight.responseCode === "O"

  const show = (fieldName: string) =>
    showField(fieldName, flight.flightMode, editMode, grcMode)
  const edit = (fieldName: string) =>
    inFocus && isFieldEditable(fieldName, flight.requestCode, editMode)
  const editable = (fieldName: string) =>
    isFieldEditable(fieldName, flight.requestCode, editMode)

  const showCaption = inFocus && !isResponse && isFirstRow && !isAdd

  const showFillDownBtn = isEdit && inFocus

  const isSwap =
    actionType === ActionType.SWAPPERIOD ||
    actionType === ActionType.SWAP ||
    actionType === ActionType.SWAPAC ||
    actionType === ActionType.SWAPLINK ||
    actionType === ActionType.SWAPARRTODEP ||
    actionType === ActionType.SWAPDEPTOARR

  const flightHasAcRegServiceType = (serviceType: string) =>
    serviceType !== "" &&
    config.showACRegForServiceTypes &&
    config.showACRegForServiceTypes?.includes(serviceType)

  const showAcRegServiceType = () => {
    if (isTurnaround) {
      return (
        flightHasAcRegServiceType(flight.serviceTypeArr) ||
        flightHasAcRegServiceType(flight.serviceTypeDep)
      )
    }

    return isArrival
      ? flightHasAcRegServiceType(flight.serviceTypeArr)
      : flightHasAcRegServiceType(flight.serviceTypeDep)
  }

  const checkUseOperatorDropdown = (): boolean =>
    (isRegMode && isAdd) || // is permission code
    (config && !config.operators.includes(""))

  const updateValuesAfterOptChange = (
    fieldNames: string[],
    opts: ValueLabel[],
    isRequired: boolean,
    resetValue: any
  ) => {
    const fieldsWithValues = fieldNames.filter(
      (fieldName) => flight[fieldName] !== null && flight[fieldName] !== ""
    )

    if (fieldsWithValues.length) {
      fieldsWithValues.forEach((fieldName) => {
        const valueExists = opts.find((opt) => flight[fieldName] === opt.value)
        if (!valueExists) {
          handleFieldChange(fieldName, resetValue, isRequired)
        }
      })
    }
  }

  const airportOpts = useMemo(() => {
    const useICAOCodeType = codeType === CodeType.ICAO

    const timeAirportSeasons = getTimeAndCodeFilteredAirportSeasons(
      useLocalTime,
      useICAOCodeType,
      airportSeasons
    )

    const timeAirportSeasonsFiltered = timeAirportSeasons.filter(
      (as) => as.season === season && (isRegMode ? as.allowGcrRegMode : true)
    )

    const opts = _.sortBy(
      timeAirportSeasonsFiltered.map((as) => ({
        label: useICAOCodeType
          ? as.airportIcao.toString()
          : as.airport.toString(),
        value: as.airport.toString()
      }))
    )
    return opts
  }, [airportSeasons, season, useLocalTime, codeType, isRegMode])

  const operatorOpts = useMemo(() => {
    const useOperatorDropdown = checkUseOperatorDropdown()
    const opts = useOperatorDropdown
      ? config.operators
          .filter((o) => o !== "")
          .map((operator) => ({ value: operator, label: operator }))
      : []
    return opts
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config])

  useEffect(() => {
    const useOperatorDropdown = checkUseOperatorDropdown()
    const showOperator = (name: string) =>
      showOperatorField(
        name,
        flight.flightMode,
        editMode,
        grcMode,
        config ? config.operators : []
      )

    if (
      !isResponse &&
      useOperatorDropdown &&
      (showOperator(operatorArr) || showOperator(operatorDep)) &&
      (editable(operatorArr) || editable(operatorDep))
    ) {
      updateValuesAfterOptChange(
        ["operatorArr", "operatorDep"],
        operatorOpts,
        true,
        ""
      )
    } else if (
      !isResponse &&
      !isEdit &&
      isRegMode &&
      (isArrival ? !showOperator(operatorArr) : !showOperator(operatorDep))
    ) {
      updateValuesAfterOptChange(
        ["operatorArr", "operatorDep"],
        operatorOpts,
        false,
        ""
      )
    }
    // eslint-disable-next-line
  }, [operatorOpts])

  const serviceTypeOpts = useMemo(() => {
    let opts: ValueLabel[]
    if (
      config &&
      config.serviceTypes &&
      config.serviceTypes.length > 0 &&
      !(config.serviceTypes.length === 1 && config.serviceTypes[0] === "")
    ) {
      opts = _.map(config.serviceTypes, (o) => {
        const dst = serviceTypes.find((s) => s.code === o)
        return { value: o, label: dst ? dst.code + " " + dst.description : o }
      })
    } else {
      opts = _.map(serviceTypes, (o) => {
        return { value: o.code, label: o.code + " " + o.description }
      })
    }
    return opts
  }, [config, serviceTypes])

  useEffect(() => {
    if (
      ((!isResponse && show(serviceTypeArr)) || show(serviceTypeDep)) &&
      (editable(serviceTypeArr) || editable(serviceTypeDep))
    ) {
      updateValuesAfterOptChange(
        ["serviceTypeArr", "serviceTypeDep"],
        serviceTypeOpts,
        true,
        ""
      )
    }
    // eslint-disable-next-line
  }, [serviceTypeOpts])

  const terminalOpts = useMemo(() => {
    const opts = config?.terminals.map((t) => ({
      label: t,
      value: t
    }))
    return opts
  }, [config])

  useEffect(() => {
    if (!isResponse && (editable(terminalArr) || editable(terminalDep))) {
      updateValuesAfterOptChange(
        ["terminalArr", "terminalDep"],
        terminalOpts,
        config?.supportsTerminalRequest && show(terminalArr),
        null
      )
    }
    // eslint-disable-next-line
  }, [terminalOpts])

  const parkingLocationOpts = useMemo(() => {
    const opts =
      config.parkingLocations.length > 1
        ? config.parkingLocations.map((parkingLocation) => ({
            value: parkingLocation,
            label: parkingLocation
          }))
        : []
    return opts
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config])

  useEffect(() => {
    if (!isResponse && show(parkingLocation)) {
      updateValuesAfterOptChange(
        ["parkingLocation"],
        parkingLocationOpts,
        parkingLocationOpts.length > 0,
        ""
      )
    }
    // eslint-disable-next-line
  }, [parkingLocationOpts])

  useEffect(() => {
    if (
      !showSection(aircraftRegistration) &&
      flight.aircraftRegistration !== "" &&
      !showAcRegServiceType()
    ) {
      handleFieldChange(FieldName.aircraftRegistration, "", false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flight.serviceTypeArr, flight.serviceTypeDep, flight.airport])

  const getFillDownBtn = (fieldName: FieldName) => {
    return (
      showFillDownBtn &&
      edit(fieldName) && (
        <div className="d-flex justify-content-center">
          <Button
            onClick={(e) => {
              e.preventDefault()
              onFillDown(fieldName)
            }}
            title="Fill Down"
            className="fill-down-btn"
          >
            <span className="fill-down-icon" />
          </Button>
        </div>
      )
    )
  }

  const getOperator = (name: string, flight: Flight, caption: string) => {
    const useOperatorDropdown = checkUseOperatorDropdown()
    const isEditable =
      inFocus && isFieldEditable(name, flight.requestCode, editMode)
    const show = showOperatorField(
      name,
      flight.flightMode,
      editMode,
      grcMode,
      config ? config.operators : []
    )

    return (
      <section
        className={
          isResponse
            ? "trans-field-w-operator-readonly"
            : "trans-field-w-operator"
        }
      >
        {show &&
          (useOperatorDropdown ? (
            <OperatorDropdownField
              name={name}
              value={flight[name]}
              isEmpty={flight[name] === ""}
              options={operatorOpts}
              caption={caption}
              readonly={!isEditable}
              required
              touched={touched}
              onChange={handleFieldChange}
              hasChanges={fieldHasChanges(name)}
              menuPortalTarget={
                editMode === EditMode.EDIT ? document.body : null
              }
            />
          ) : (
            <OperatorField
              required
              touched={touched}
              name={name}
              value={flight[name]}
              isEmpty={flight[name] === ""}
              caption={caption}
              readonly={!isEditable}
              hasChanges={fieldHasChanges(name)}
              onChange={handleFieldChange}
            />
          ))}
      </section>
    )
  }

  const getServiceType = (name: FieldName, flight: Flight, caption: string) => {
    const isEditable =
      inFocus && isFieldEditable(name, flight.requestCode, editMode)
    const show = showField(name, flight.flightMode, editMode, grcMode)
    return (
      <div
        className={
          editMode === EditMode.RESPONSE
            ? "trans-field-w-service-type-readonly"
            : "trans-field-w-service-type "
        }
      >
        {show && (
          <Fragment>
            <ServiceTypeDropdownField
              name={name}
              required
              touched={touched}
              value={flight[name]}
              isEmpty={flight[name] === ""}
              caption={caption}
              options={serviceTypeOpts}
              readonly={!isEditable}
              hasChanges={fieldHasChanges(name)}
              menuPortalTarget={
                editMode === EditMode.EDIT ? document.body : null
              }
              onChange={handleFieldChange}
              formatOptionLabel={(opt, { context }) => {
                return context === "menu" ? opt.label : opt.value
              }}
            />
            {getFillDownBtn(name)}
          </Fragment>
        )}
      </div>
    )
  }

  const getACRegField = (key: number) => {
    const isOptional = !isRegMode

    return (
      (showSection(aircraftRegistration) ||
        customShowLabelConfig.serviceTypeAcReg.showLabelSection) && (
        <section
          key={key}
          className="ocs-field-item trans-section-ac-reg trans-field-w-ac-reg"
        >
          {(customShowLabelConfig.serviceTypeAcReg.showLabelSection
            ? showAcRegServiceType()
            : show(aircraftRegistration)) && (
            <AircraftRegistrationField
              value={flight.aircraftRegistration}
              caption={!showCaption ? null : acReg}
              readonly={!edit(aircraftRegistration)}
              hasChanges={fieldHasChanges(aircraftRegistration)}
              onChange={handleFieldChange}
              required={!isOptional}
              touched={touched}
              isEmpty={!isOptional && flight.aircraftRegistration === ""}
            />
          )}
        </section>
      )
    )
  }

  const getMinGroundTimeField = (key: number) => {
    return (
      showOptionalFields &&
      showSection(minGroundTime) && (
        <section
          key={key}
          className="ocs-field-item trans-section-mgt trans-field-w-mgt d-flex align-items-end"
        >
          {show(minGroundTime) && (
            <MinGroundTimeFieldN
              caption={!showCaption ? null : mgt}
              value={flight.minGroundTime}
              readonly={!edit(minGroundTime)}
              hasChanges={fieldHasChanges(minGroundTime)}
              onChange={handleFieldChange}
            />
          )}
        </section>
      )
    )
  }

  const errorMsg = airportError
    ? `${flight.airport} is ${lockedOrDisabledArr.errorMsg}`
    : null

  const getAirportField = (key: number) => {
    return (
      showSection(airport) && (
        <section
          key={key}
          className={
            "ocs-field-item trans-section-airport " +
            (edit("airport") || isAdd
              ? useICAOCodeType
                ? "trans-field-w-airport-icao"
                : "trans-field-w-airport"
              : useICAOCodeType
                ? "trans-field-w-airport-readonly-icao"
                : "trans-field-w-airport-readonly")
          }
        >
          {show(airport) && (
            <DropdownField
              name={airport}
              required
              errorMsg={errorMsg}
              touched={touched}
              caption={!showCaption ? null : FlightText.airport}
              clearable={false}
              closeOnSelect
              readonly={!edit(airport)}
              labelOnReadOnly={useICAOCodeType}
              options={airportOpts}
              autoFocus
              value={flight.airport}
              hasError={airportError}
              onChange={handleFieldChange}
              menuPortalTarget={
                editMode === EditMode.EDIT ? document.body : null
              }
            />
          )}
        </section>
      )
    )
  }

  const getRequestCodeField = (key: number) => {
    return (
      showSection(requestCode) && (
        <section
          key={key}
          className="ocs-field-item trans-section-request-code trans-field-w-request-code"
        >
          {show(requestCode) && (
            <RequestCodeField
              value={flight.requestCode}
              caption={!showCaption ? null : FlightText.requestCode}
              readonly={!edit(requestCode)}
              hasChanges={fieldHasChanges(requestCode)}
              onChange={handleFieldChange}
            />
          )}
        </section>
      )
    )
  }

  const getOperatorFields = (key: number) => {
    return (
      (showSection(operatorArr) || showSection(operatorDep)) && (
        <section key={key} className="ocs-field-item trans-section-operator">
          {getOperator(operatorArr, flight, !showCaption ? null : operator)}
          <div className={isTurnaround ? " ocs-mt-12" : ""}>
            {getOperator(
              operatorDep,
              flight,
              isTurnaround || !showCaption ? null : operator
            )}
          </div>
        </section>
      )
    )
  }

  const getServiceNoFields = (key: number) => {
    return (
      (showSection(serviceNoArr) || showSection(serviceNoDep)) && (
        <section key={key} className="ocs-field-item trans-section-service-no">
          <div className="trans-field-w-service-no">
            {show(serviceNoArr) && (
              <ServiceNumberField
                name={serviceNoArr}
                required
                touched={touched}
                value={flight.serviceNoArr}
                caption={!showCaption ? null : serviceNo}
                readonly={!edit(serviceNoArr)}
                hasChanges={fieldHasChanges(serviceNoArr)}
                onChange={handleFieldChange}
                validator={serviceNoRegex}
                validationError={ErrorText.serviceNoErrorText}
                valueIsEmpty={flight.serviceNoArr === ""}
                isEmpty={flight.serviceNoArr === ""}
              />
            )}
          </div>
          <div
            className={
              "trans-field-w-service-no" + (isTurnaround ? " ocs-mt-12" : "")
            }
          >
            {show(serviceNoDep) && (
              <ServiceNumberField
                name={serviceNoDep}
                required
                touched={touched}
                caption={isTurnaround || !showCaption ? null : serviceNo}
                value={flight.serviceNoDep}
                isEmpty={flight.serviceNoDep === ""}
                readonly={!edit(serviceNoDep)}
                hasChanges={fieldHasChanges(serviceNoDep)}
                onChange={handleFieldChange}
                validator={serviceNoRegex}
                validationError={ErrorText.serviceNoErrorText}
                valueIsEmpty={flight.serviceNoDep === ""}
              />
            )}
          </div>
        </section>
      )
    )
  }

  const getServiceTypeFields = (key: number) => {
    const stcCaption = pageGroup === PageGroup.HANDLING ? servType : st
    return (
      (showSection(serviceTypeArr) || showSection(serviceTypeDep)) && (
        <section
          key={key}
          className="ocs-field-item trans-section-service-type"
        >
          {getServiceType(
            serviceTypeArr,
            flight,
            !showCaption ? null : stcCaption
          )}
          <div className={isTurnaround ? " ocs-mt-12" : ""}>
            {getServiceType(
              serviceTypeDep,
              flight,
              isTurnaround || !showCaption ? null : stcCaption
            )}
          </div>
        </section>
      )
    )
  }

  const getDateFields = (key: number) => {
    return (
      <section
        key={key}
        className={
          "trans-section-date " +
          (isDateRange
            ? "ocs-mr-4 " +
              (isResponse
                ? "trans-field-w-date-range-readonly"
                : "trans-field-w-date-range")
            : "ocs-mr-12 trans-field-w-date")
        }
      >
        {isDateRange ? (
          <SeasonDateRange
            required
            touched={touched}
            validationError={ErrorText.noDate}
            dateRangeStartValue={flight.startDate}
            dateRangeEndValue={flight.endDate}
            showCaption={showCaption}
            readonly={!edit(startDate)}
            hasChanges={fieldHasChanges(startDate) || fieldHasChanges(endDate)}
            onChange={handleFieldChange}
            showIcon={false}
            dropdownId={"transItem-" + transId}
            autoFocus={isEdit && idx === 0}
            placement={placement}
            hasError={dateRangeError}
            isAddPage={isAdd}
          />
        ) : (
          <SeasonDatePicker
            name={startDate}
            isAddPage={isAdd}
            required
            touched={touched}
            validationError={ErrorText.noDate}
            value={flight.startDate}
            isEmpty={flight.startDate === null || flight.startDate === ""}
            caption={showCaption ? "Date" : null}
            onChange={handleFieldChange}
            dropdownId={"transItem-" + transId}
            hasChanges={fieldHasChanges(startDate)}
            readonly={!edit("startDateSingle")}
            placement={placement}
            onSelectDate={() => setHasSelectedStartDate(true)}
          />
        )}
        {showSection(departureDate) && (
          <div className="ocs-mt-12">
            <SeasonDepartureDatePicker
              turnaroundDays={flight.turnaroundDays}
              startDate={flight.startDate}
              onChange={handleFieldChange}
              readonly={!edit(departureDate)}
              hasChanges={fieldHasChanges(turnaroundDays)}
              placement={placement}
              dateHasError={dateHasError}
              dateHasErrText={dateHasErrText}
              hasSelectedStartDate={hasSelectedStartDate}
            />
          </div>
        )}
      </section>
    )
  }

  const getReqDate = (key: number) => {
    return (
      (showSection(requiredDateArr) || showSection(requiredDateDep)) && (
        <section key={key} className="trans-field-w-req-date ocs-mr-12">
          <SeasonDatePicker
            name={requiredDateArr}
            value={flight.requiredDateArr}
            caption={showCaption ? FlightText.reqDate : null}
            onChange={handleFieldChange}
            hasChanges={fieldHasChanges(requiredDateArr)}
            readonly
          />
          <div className="ocs-mt-12">
            <SeasonDatePicker
              name={requiredDateDep}
              value={flight.requiredDateDep}
              caption={isTurnaround || !showCaption ? null : FlightText.reqDate}
              onChange={handleFieldChange}
              hasChanges={fieldHasChanges(requiredDateDep)}
              readonly
            />
          </div>
        </section>
      )
    )
  }

  const getDoopField = (key: number) => {
    return (
      showSection(doop) && (
        <section
          key={key}
          className="ocs-field-item trans-section-doop trans-field-w-doop"
        >
          {show(doop) && (
            //Doop
            <DOOPField
              value={flight.doop}
              caption={!showCaption ? null : FlightText.doop}
              readonly={!edit(doop)}
              hasChanges={fieldHasChanges(doop)}
              onChange={handleFieldChange}
              touched={touched}
            />
          )}
        </section>
      )
    )
  }

  const getWoopField = (key: number) => {
    return (
      showOptionalFields &&
      showSection(woop) && (
        <section
          key={key}
          className="ocs-field-item trans-section-woop trans-field-w-woop"
        >
          {show(woop) && (
            <WOOPFieldN
              name={woop}
              caption={!showCaption ? null : f}
              value={flight.woop}
              readonly={!edit(woop)}
              hasChanges={fieldHasChanges(woop)}
              onChange={handleFieldChange}
              touched={touched}
              isEmpty={flight.woop === null}
              required={isEditSeries}
            />
          )}
        </section>
      )
    )
  }

  const getClearedTimeFields = (key: number) => {
    return (
      (showSection(clearedTimeArr) || showSection(clearedTimeDep)) && (
        //Time
        <section
          key={key}
          className="ocs-field-item trans-section-cleared-time"
        >
          <section className="trans-field-w-cleared-time">
            {show(clearedTimeArr) && (
              <TimeField
                name={clearedTimeArr}
                allow9999time={editMode === EditMode.EDIT}
                required
                touched={touched}
                caption={!showCaption ? null : clearTime}
                readonly={!edit(clearedTimeArr)}
                hasChanges={fieldHasChanges(clearedTimeArr) || isOffer}
                value={flight.clearedTimeArr}
                onChange={handleFieldChange}
                isEmpty={flight.clearedTimeArr === ""}
              />
            )}
          </section>
          <section
            className={
              "trans-field-w-cleared-time" + (isTurnaround ? " ocs-mt-12" : "")
            }
          >
            {show(clearedTimeDep) && (
              <TimeField
                name={clearedTimeDep}
                allow9999time={editMode === EditMode.EDIT}
                required
                touched={touched}
                caption={isTurnaround || !showCaption ? null : clearTime}
                value={flight.clearedTimeDep}
                readonly={!edit(clearedTimeDep)}
                hasChanges={fieldHasChanges(clearedTimeDep) || isOffer}
                onChange={handleFieldChange}
                isEmpty={flight.clearedTimeDep === ""}
              />
            )}
          </section>
        </section>
      )
    )
  }

  const getReqTimeFields = (key: number) => {
    return (
      (showSection(requiredTimeArr) || showSection(requiredTimeDep)) && (
        <section key={key} className="ocs-field-item trans-section-req-time">
          {show(requiredTimeArr) && (
            <section className="trans-field-w-req-time">
              <TimeField
                name={requiredTimeArr}
                caption={!showCaption ? null : reqTime}
                value={flight.requiredTimeArr}
                readonly={!edit(requiredTimeArr)}
                hasChanges={fieldHasChanges(requiredTimeArr)}
                onChange={handleFieldChange}
                touched={touched}
              />
            </section>
          )}
          {show(requiredTimeDep) && (
            <section
              className={
                "trans-field-w-req-time" + (isTurnaround ? " ocs-mt-12" : "")
              }
            >
              <TimeField
                name={requiredTimeDep}
                caption={isTurnaround || !showCaption ? null : reqTime}
                value={flight.requiredTimeDep}
                readonly={!edit(requiredTimeDep)}
                hasChanges={fieldHasChanges(requiredTimeDep)}
                onChange={handleFieldChange}
                touched={touched}
              />
            </section>
          )}
        </section>
      )
    )
  }

  const getReasonCodeFields = (key: number) => {
    return (
      (showSection(reasonCodeArr) || showSection(reasonCodeDep)) && (
        <section key={key} className="ocs-field-item trans-section-reason-code">
          {show(reasonCodeArr) && (
            <section className="trans-field-w-reason-code">
              <TextField
                name={reasonCodeArr}
                caption={!showCaption ? null : reason}
                value={flight.reasonCodeArr}
                isEmpty={flight.reasonCodeArr === ""}
                readonly={!edit(reasonCodeArr)}
                uppercase
              />
            </section>
          )}
          {show(reasonCodeDep) && (
            <section
              className={
                "trans-field-w-reason-code" + (isTurnaround ? " ocs-mt-12" : "")
              }
            >
              <TextField
                name={reasonCodeDep}
                caption={isTurnaround || !showCaption ? null : reason}
                value={flight.reasonCodeDep}
                isEmpty={flight.reasonCodeDep === ""}
                readonly={!edit(reasonCodeDep)}
                uppercase
              />
            </section>
          )}
        </section>
      )
    )
  }

  const getTurnaroundDaysField = (key: number) => {
    return (
      showOptionalFields &&
      showSection(turnaroundDays) && (
        <section
          key={key}
          className="ocs-field-item trans-section-turnaround-days trans-field-w-turnaround-days d-flex align-items-end"
        >
          {show(turnaroundDays) && (
            <OvernightIndicatorFieldN
              caption={!showCaption ? null : o}
              name={turnaroundDays}
              value={flight.turnaroundDays}
              readonly={!edit(turnaroundDays)}
              hasChanges={fieldHasChanges(turnaroundDays)}
              onChange={handleFieldChange}
            />
          )}
        </section>
      )
    )
  }

  const getAircraftTypeField = (key: number) => {
    return (
      showSection(aircraftType) && (
        // AC Type
        <section
          key={key}
          className="ocs-field-item trans-section-ac-type trans-field-w-ac-type"
        >
          {show(aircraftType) && (
            <Fragment>
              <AircraftTypeField
                required
                touched={touched}
                caption={!showCaption ? null : acType}
                value={flight.aircraftType}
                readonly={!edit(aircraftType)}
                codeType={codeType}
                hasChanges={fieldHasChanges(aircraftType)}
                onChange={handleFieldChange}
                isEmpty={flight.aircraftType === ""}
              />
              {getFillDownBtn(aircraftType)}
            </Fragment>
          )}
        </section>
      )
    )
  }

  const getNumSeatsField = (key: number) => {
    return (
      showSection(numSeats) && (
        // Seats
        <section
          key={key}
          className="ocs-field-item trans-section-seats trans-field-w-seats"
        >
          {show(numSeats) && (
            <Fragment>
              <SeatsField
                required
                touched={touched}
                caption={!showCaption ? null : seats}
                name={numSeats}
                value={flight.numSeats}
                readonly={!edit(numSeats)}
                hasChanges={fieldHasChanges(numSeats)}
                onChange={handleFieldChange}
                isEmpty={flight.numSeats === ""}
              />
              {getFillDownBtn(numSeats)}
            </Fragment>
          )}
        </section>
      )
    )
  }

  const getOriginField = (key: number) => {
    return (
      showSection(originStation) && (
        <section
          key={key}
          className={
            "ocs-field-item " +
            (useICAOCodeType
              ? "trans-field-w-station-icao"
              : "trans-field-w-station")
          }
        >
          {/* Dest */}
          {show(originStation) && (
            <Fragment>
              <StationField
                required
                touched={touched}
                name={originStation}
                caption={
                  !showCaption
                    ? null
                    : isTurnaround
                      ? `${origin}/${dest}`
                      : origin
                }
                value={flight.originStation}
                codeType={codeType}
                readonly={!edit(originStation)}
                hasChanges={fieldHasChanges(originStation)}
                onChange={handleFieldChange}
                isEmpty={flight.originStation === ""}
              />
              {getFillDownBtn(originStation)}
            </Fragment>
          )}
        </section>
      )
    )
  }

  const getLastField = (key: number) => {
    return (
      showOptionalFields &&
      showSection(lastStation) && (
        <section
          key={key}
          className={
            "ocs-field-item " +
            (useICAOCodeType
              ? "trans-field-w-station-icao"
              : "trans-field-w-station")
          }
        >
          {show(lastStation) && (
            <Fragment>
              <StationField
                name={lastStation}
                caption={
                  !showCaption ? null : isTurnaround ? `${last}/${next}` : last
                }
                value={flight.lastStation}
                codeType={codeType}
                readonly={!edit(lastStation)}
                hasChanges={fieldHasChanges(lastStation)}
                onChange={handleFieldChange}
                touched={touched}
              />
              {getFillDownBtn(lastStation)}
            </Fragment>
          )}
        </section>
      )
    )
  }

  const getDestinationField = (key: number) => {
    return (
      showSection(destinationStation) && (
        <section
          key={key}
          className={
            "ocs-field-item d-flex flex-column justify-content-end " +
            (useICAOCodeType
              ? "trans-field-w-station-icao"
              : "trans-field-w-station")
          }
        >
          {show(destinationStation) && (
            <Fragment>
              <StationField
                required
                touched={touched}
                name={destinationStation}
                caption={isTurnaround || !showCaption ? null : dest}
                value={flight.destinationStation}
                codeType={codeType}
                readonly={!edit(destinationStation)}
                hasChanges={fieldHasChanges(destinationStation)}
                onChange={handleFieldChange}
                isEmpty={flight.destinationStation === ""}
              />
              {getFillDownBtn(destinationStation)}
            </Fragment>
          )}
        </section>
      )
    )
  }

  const getNextField = (key: number) => {
    return (
      showOptionalFields &&
      showSection(nextStation) && (
        <section
          key={key}
          className={
            "ocs-field-item d-flex flex-column justify-content-end " +
            (useICAOCodeType
              ? "trans-field-w-station-icao"
              : "trans-field-w-station")
          }
        >
          {show(nextStation) && (
            <Fragment>
              <StationField
                name={nextStation}
                caption={isTurnaround || !showCaption ? null : next}
                value={flight.nextStation}
                codeType={codeType}
                readonly={!edit(nextStation)}
                hasChanges={fieldHasChanges(nextStation)}
                onChange={handleFieldChange}
                touched={touched}
              />
              {getFillDownBtn(nextStation)}
            </Fragment>
          )}
        </section>
      )
    )
  }

  const getOrigDestFields = (key: number) => {
    return (
      (showSection(originStation) || showSection(destinationStation)) && (
        <section key={key} className="ocs-field-item trans-section-orig-dest">
          <section
            className={
              useICAOCodeType
                ? "trans-field-w-station-icao"
                : "trans-field-w-station"
            }
          >
            {/* Dest */}
            {show(originStation) && (
              <Fragment>
                <StationField
                  required
                  touched={touched}
                  name={originStation}
                  caption={
                    !showCaption
                      ? null
                      : isTurnaround
                        ? `${origin}/${dest}`
                        : origin
                  }
                  value={flight.originStation}
                  codeType={codeType}
                  readonly={!edit(originStation)}
                  hasChanges={fieldHasChanges(originStation)}
                  onChange={handleFieldChange}
                  isEmpty={flight.originStation === ""}
                />
                {getFillDownBtn(originStation)}
              </Fragment>
            )}
          </section>
          <section
            className={
              (useICAOCodeType
                ? "trans-field-w-station-icao"
                : "trans-field-w-station") + (isTurnaround ? " ocs-mt-12" : "")
            }
          >
            {show(destinationStation) && (
              <Fragment>
                <StationField
                  required
                  touched={touched}
                  name={destinationStation}
                  caption={isTurnaround || !showCaption ? null : dest}
                  value={flight.destinationStation}
                  codeType={codeType}
                  readonly={!edit(destinationStation)}
                  hasChanges={fieldHasChanges(destinationStation)}
                  onChange={handleFieldChange}
                  isEmpty={flight.destinationStation === ""}
                />
                {getFillDownBtn(destinationStation)}
              </Fragment>
            )}
          </section>
        </section>
      )
    )
  }

  const getLastNextFields = (key: number) => {
    return (
      showOptionalFields &&
      (showSection(lastStation) || showSection(nextStation)) && (
        <section key={key} className="ocs-field-item trans-section-last-next">
          <section
            className={
              useICAOCodeType
                ? "trans-field-w-station-icao"
                : "trans-field-w-station"
            }
          >
            {show(lastStation) && (
              <Fragment>
                <StationField
                  name={lastStation}
                  caption={
                    !showCaption
                      ? null
                      : isTurnaround
                        ? `${last}/${next}`
                        : last
                  }
                  value={flight.lastStation}
                  codeType={codeType}
                  readonly={!edit(lastStation)}
                  hasChanges={fieldHasChanges(lastStation)}
                  onChange={handleFieldChange}
                  touched={touched}
                />
                {getFillDownBtn(lastStation)}
              </Fragment>
            )}
          </section>
          <section
            className={
              (useICAOCodeType
                ? "trans-field-w-station-icao"
                : "trans-field-w-station") + (isTurnaround ? " ocs-mt-12" : "")
            }
          >
            {show(nextStation) && (
              <Fragment>
                <StationField
                  name={nextStation}
                  caption={isTurnaround || !showCaption ? null : next}
                  value={flight.nextStation}
                  codeType={codeType}
                  readonly={!edit(nextStation)}
                  hasChanges={fieldHasChanges(nextStation)}
                  onChange={handleFieldChange}
                  touched={touched}
                />
                {getFillDownBtn(nextStation)}
              </Fragment>
            )}
          </section>
        </section>
      )
    )
  }

  const getOfferTimeFields = (key: number) => {
    return (
      showOptionalFields &&
      (showSection(offerTimeArr) || showSection(offerTimeDep)) && (
        <section key={key} className="trans-section-search-time ocs-mr-4">
          <section className="trans-field-w-search-time-range">
            {show(offerTimeArr) && (
              <TimeRangeField
                caption={!showCaption ? null : searchTimeRange}
                fromName={offerTimeArrFrom}
                fromValue={flight.offerTimeArrFrom}
                toName={offerTimeArrTo}
                toValue={flight.offerTimeArrTo}
                onChange={handleFieldChange}
                readonly={!edit(offerTimeArr)}
                touched={touched}
                showTimeRangeError={showTimeRangeError}
                hasChanges={
                  fieldHasChanges(offerTimeArrTo) ||
                  fieldHasChanges(offerTimeArrFrom)
                }
              />
            )}
          </section>
          <section
            className={
              "trans-field-w-search-time-range" +
              (isTurnaround ? " ocs-mt-12" : "")
            }
          >
            {show(offerTimeDep) && (
              <TimeRangeField
                caption={isTurnaround || !showCaption ? null : searchTimeRange}
                fromName={offerTimeDepFrom}
                fromValue={flight.offerTimeDepFrom}
                toName={offerTimeDepTo}
                toValue={flight.offerTimeDepTo}
                onChange={handleFieldChange}
                readonly={!edit(offerTimeDep)}
                touched={touched}
                hasChanges={
                  fieldHasChanges(offerTimeDepTo) ||
                  fieldHasChanges(offerTimeDepFrom)
                }
                showTimeRangeError={showTimeRangeError}
              />
            )}
          </section>
        </section>
      )
    )
  }

  const getHandlerField = (key: number) => {
    const handlerOpts = airportSeasons[0]?.handlers.map((handler) => ({
      value: handler,
      label: handler
    }))

    return (
      showSection(handler) && (
        // Handling
        <section key={key} className="ocs-field-item trans-field-w-handler">
          {show(handler) && (
            <Fragment>
              <HandlerField
                value={flight.handler}
                onChange={handleFieldChange}
                caption={!showCaption ? null : FlightText.handler}
                readonly={!edit(handler)}
                options={handlerOpts}
                required
                isEmpty={flight.handler === ""}
                touched={touched}
                hasChanges={fieldHasChanges(handler)}
              />
            </Fragment>
          )}
        </section>
      )
    )
  }

  const getParkingLocationField = (key: number) => {
    return (
      showSection(parkingLocation) &&
      customShowLabelConfig.parkingLocation.showLabelSection && (
        // Parking
        <section key={key} className="ocs-field-item trans-field-w-parking-loc">
          {show(parkingLocation) && parkingLocationOpts.length > 0 && (
            <Fragment>
              <ParkingLocationField
                value={flight.parkingLocation}
                options={parkingLocationOpts}
                onChange={handleFieldChange}
                caption={!showCaption ? null : FlightText.parkLoc}
                readonly={!edit(parkingLocation)}
                hasChanges={fieldHasChanges(parkingLocation)}
                required
                isEmpty={flight.parkingLocation === ""}
                menuPortalTarget={
                  editMode === EditMode.EDIT ? document.body : null
                }
                touched={touched}
              />
            </Fragment>
          )}
        </section>
      )
    )
  }

  const getNoiseField = (key: number) => {
    return (
      showSection(noiseCertificate) && (
        // Noise
        <section key={key} className="ocs-field-item trans-field-w-noise">
          {show(noiseCertificate) && (
            <NoiseCertificateField
              value={flight.noiseCertificate}
              caption={!showCaption ? null : FlightText.noise}
              onChange={handleFieldChange}
              readonly={!edit(noiseCertificate)}
              hasChanges={fieldHasChanges(noiseCertificate)}
            />
          )}
        </section>
      )
    )
  }

  const getVfrField = (key: number) => {
    return (
      showSection(vfr) && (
        // VFR
        <section
          key={key}
          className="ocs-field-item trans-field-w-flight-rules"
        >
          {show(vfr) && (
            <DropdownBooleanField
              caption={!showCaption ? null : FlightText.vfrCaption}
              name={FieldName.vfr}
              booleanValue={flight.vfr}
              booleanOptions={[
                { label: FlightText.vfr, value: true },
                { label: FlightText.ifr, value: false }
              ]}
              onChangeBooleanField={handleFieldChange}
              readonly={!edit(vfr)}
              isEmpty={flight.vfr === null}
              touched={touched}
              hasChanges={fieldHasChanges(vfr)}
            />
          )}
        </section>
      )
    )
  }

  const getTowbarField = (key: number) => {
    return (
      showSection(towbar) && (
        // towbar
        <section key={key} className="ocs-field-item trans-field-w-towbar">
          {show(towbar) && (
            <DropdownBooleanField
              caption={!showCaption ? null : "Towbar"}
              name={FieldName.towbar}
              booleanValue={flight.towbar}
              booleanOptions={[
                { label: "Yes", value: true },
                { label: "No", value: false }
              ]}
              onChangeBooleanField={handleFieldChange}
              readonly={!edit(towbar)}
              isEmpty={flight.towbar === null}
              touched={touched}
              hasChanges={fieldHasChanges(towbar)}
            />
          )}
        </section>
      )
    )
  }

  const getLoungeField = (key: number) => {
    return (
      showOptionalFields &&
      showSection(gabaLounge) && (
        // Lounge
        <section key={key} className="ocs-field-item trans-field-w-lounge">
          {show(gabaLounge) && (
            <RadioBooleanField
              caption={!showCaption ? null : "Lounge"}
              name={FieldName.gabaLounge}
              value={flight.gabaLounge}
              options={[
                { label: "Yes", value: true },
                { label: "No", value: false }
              ]}
              onChange={handleFieldChange}
              readonly={!edit(gabaLounge)}
              hasChanges={fieldHasChanges(gabaLounge)}
              className="h-auto"
            />
          )}
        </section>
      )
    )
  }

  const getRefuellingField = (key: number) => {
    return (
      showOptionalFields &&
      showSection(refuelling) && (
        // Refuelling
        <section key={key} className="ocs-field-item trans-field-w-refuel">
          {show(refuelling) && (
            <RadioBooleanField
              caption={!showCaption ? null : "Refuel"}
              name={FieldName.refuelling}
              value={flight.refuelling}
              options={[
                { label: "Yes", value: true },
                { label: "No", value: false }
              ]}
              onChange={handleFieldChange}
              readonly={!edit(refuelling)}
              hasChanges={fieldHasChanges(refuelling)}
              className="h-auto"
            />
          )}
        </section>
      )
    )
  }

  const getMTOWField = (key: number) => {
    return (
      showSection(mtow) && (
        // Handling
        <section key={key} className="ocs-field-item trans-field-w-mtow">
          {show(mtow) && (
            <Fragment>
              <MtowField
                value={flight.mtow}
                onChange={handleFieldChange}
                caption={!showCaption ? null : FlightText.mtow}
                readonly={!edit(mtow)}
                required
                isEmpty={flight.mtow === null}
                touched={touched}
              />

              {/* {getFillDownBtn(numSeats)} */}
            </Fragment>
          )}
        </section>
      )
    )
  }

  const getOperatorTextField = (key: number) => {
    return (
      showSection(operatorText) && (
        // Handling
        <section
          key={key}
          className="ocs-field-item trans-field-w-operator-text"
        >
          {show(operatorText) && (
            <Fragment>
              <OpTextField
                value={flight.operatorText}
                onChange={handleFieldChange}
                caption={!showCaption ? null : FlightText.operatorTextCaption}
                readonly={!edit(operatorText)}
                required
                isEmpty={flight.operatorText === ""}
                touched={touched}
              />
              {/* {getFillDownBtn(numSeats)} */}
            </Fragment>
          )}
        </section>
      )
    )
  }

  const getTerminalFields = (key: number) => {
    return (
      customShowLabelConfig.terminal.showLabelSection &&
      (showSection(terminalArr) || showSection(terminalDep)) && (
        <section
          key={key}
          className={
            "ocs-field-item" +
            (isTurnaround && !show(terminalArr)
              ? " d-flex flex-column justify-content-end"
              : "")
          }
        >
          <section className="trans-field-w-terminal">
            {config?.supportsTerminalRequest && show(terminalArr) && (
              <TerminalDropdownField
                name={terminalArr}
                caption={!showCaption ? null : FlightText.terminal}
                options={terminalOpts}
                value={flight.terminalArr}
                isEmpty={flight.terminalArr === null}
                required
                touched={touched}
                readonly={!edit(terminalArr)}
                onChange={handleFieldChange}
                hasChanges={fieldHasChanges(terminalArr)}
              />
            )}
          </section>
          <section
            className={
              "trans-field-w-terminal" +
              (isTurnaround && show(terminalArr) ? " ocs-mt-12" : "")
            }
          >
            {config?.supportsTerminalRequest && show(terminalDep) && (
              <TerminalDropdownField
                name={terminalDep}
                caption={
                  isTurnaround || !showCaption ? null : FlightText.terminal
                }
                options={terminalOpts}
                value={flight.terminalDep}
                isEmpty={flight.terminalDep === null}
                required
                touched={touched}
                readonly={!edit(terminalDep)}
                onChange={handleFieldChange}
                hasChanges={fieldHasChanges(terminalDep)}
              />
            )}
          </section>
        </section>
      )
    )
  }

  const fieldMap = {
    [aircraftRegistration]: getACRegField,
    [minGroundTime]: getMinGroundTimeField,
    [airport]: getAirportField,
    [requestCode]: getRequestCodeField,
    [operatorArr]: getOperatorFields,
    [serviceNoArr]: getServiceNoFields,
    [serviceTypeArr]: getServiceTypeFields,
    [startDate]: getDateFields,
    [reqDate]: getReqDate,
    [doop]: getDoopField,
    [woop]: getWoopField,
    [clearedTimeArr]: getClearedTimeFields,
    [requiredTimeArr]: getReqTimeFields,
    [reasonCodeArr]: getReasonCodeFields,
    [turnaroundDays]: getTurnaroundDaysField,
    [aircraftType]: getAircraftTypeField,
    [numSeats]: getNumSeatsField,
    [origDest]: getOrigDestFields,
    [lastNext]: getLastNextFields,
    [originStation]: getOriginField,
    [lastStation]: getLastField,
    [destinationStation]: getDestinationField,
    [nextStation]: getNextField,
    [offerTimeArr]: getOfferTimeFields,
    [handler]: getHandlerField,
    [parkingLocation]: getParkingLocationField,
    [noiseCertificate]: getNoiseField,
    [vfr]: getVfrField,
    [towbar]: getTowbarField,
    [gabaLounge]: getLoungeField,
    [refuelling]: getRefuellingField,
    [mtow]: getMTOWField,
    [operatorText]: getOperatorTextField,
    [terminalArr]: getTerminalFields
  }

  const handleGetFields = () => {
    let fields = []
    const orderFormat = isSSIMVIew ? "SSIMOrder" : "categoryOrder"
    let orderedFields = _.cloneDeep(fieldOrder).sort((a, b) => {
      return (
        ((isGaba || isHandling) && a.gabaOrder ? a.gabaOrder : a[orderFormat]) -
        ((isGaba || isHandling) && b.gabaOrder ? b.gabaOrder : b[orderFormat])
      )
    })

    orderedFields.forEach((f, idx) => {
      if (fieldMap[f.type] && f[orderFormat] >= 0) {
        fields.push(fieldMap[f.type](idx))
      }
    })

    return fields
  }

  return (
    <div
      key={idx}
      className={
        "d-flex ocs-transaction-item-flight" +
        (hasMultiFlightModes || isSwap
          ? " ocs-transaction-item-multi-flights"
          : isOffer
            ? " ocs-transaction-item-flight-offer"
            : "")
      }
    >
      {hasMultiFlightModes && (
        <div className="d-flex align-items-center ocs-transaction-item-flightmode multi-flightmodes">
          <p>{getFlightModeText(flight.flightMode)}</p>
        </div>
      )}
      {hasMultiFlightModes && (
        <span className="ocs-transaction-item-divider code-divider multi-flightmodes-code-divider">
          <div />
        </span>
      )}
      {editMode === EditMode.EDIT && (
        <div className="d-flex align-items-center ocs-transaction-item-code">
          <p>{flight.requestCode}</p>
        </div>
      )}
      {isResponse && (
        <div
          className={
            "d-flex align-items-center ocs-transaction-item-code " +
            (isShl ? "shl-response-code justify-content-between" : "")
          }
        >
          <p>{flight.responseCode}</p>
          {isShl && (
            <>
              <p>:</p>
              <p>{flight.requestCode}</p>
            </>
          )}
        </div>
      )}
      <div
        className={
          "d-flex ocs-transaction-flight-fields" +
          (idx === 0 ? " first-flight" : "")
        }
      >
        {isResponse && isInitResponse && (
          <div className="trans-field-w-reply-code ocs-mr-12 ocs-ml-12">
            {flight.replyOptions.length > 0 && (
              <ReplyCodeField
                replyCode={flight.replyCode}
                replyOptions={flight.replyOptions}
                onChangeCode={handleFieldChange}
              />
            )}
          </div>
        )}
        {!isResponse && (
          <div
            className={
              "ocs-transaction-flight-icons " +
              (isAdd
                ? isSSIMVIew
                  ? "with-caption"
                  : "with-headings"
                : showCaption
                  ? "with-caption"
                  : "")
            }
          >
            <div className="d-flex align-items-center">
              <Icon className="ocs-md-trans-icon">
                {isTurnaround || isArrival ? "flight_land" : "flight_takeoff"}
              </Icon>
            </div>
            {isTurnaround && (
              <div className="d-flex align-items-center ocs-mt-12">
                <Icon className="ocs-md-trans-icon">flight_takeoff</Icon>
              </div>
            )}
          </div>
        )}
        <div>
          {isAdd && (
            <TransactionFlightHeaders
              editMode={EditMode.ADD}
              pageGroup={pageGroup}
              codeType={codeType}
              flightMode={flight.flightMode}
              grcMode={grcMode}
              customShowLabelConfig={{
                ...customShowLabelConfig,
                serviceTypeAcReg: {
                  ...customShowLabelConfig.serviceTypeAcReg,
                  showLabel: showAcRegServiceType()
                }
              }}
              showOperatorLabel={
                isRegMode ? config?.operators.length > 1 : undefined
              }
            />
          )}
          <div
            className={
              "d-flex" + (isResponse && !isInitResponse ? " ocs-ml-12" : "")
            }
          >
            {handleGetFields()}
          </div>
        </div>
      </div>
    </div>
  )
}

export default TransactionFlight
